import type { CategoriesApi } from "@/v2/api/metadata/categories";
import type { PlayersApi } from "@/v2/api/metadata/players";
import type { SportsApi } from "@/v2/api/metadata/sports";
import type { TeamsApi } from "@/v2/api/metadata/teams";
import type { TournamentsApi } from "@/v2/api/metadata/tournaments";
import type { DialogConfig } from "@/v2/framework/dialog/types";
import { buildAddSupplementalNamesPayload } from "@/v2/kits/supplemental-names";
import { notify } from "@/v2/notifications/notify";
import { NXGDSStatusVariant } from "@nsftx/seven-nxg-design-system-backoffice";
import { t } from "@/services/i18n";
import { PLAYER_MAX_POSITION, playerGamesStatusOptions, playerStatusOptions } from "../../shared";
import {
  buildAddPlayerAssignmentsPayload,
  hasPlayerAssignment,
} from "./assignments";

function positionValidator(value: unknown) {
  if (value == null || value === "") return undefined;
  if (Number(value) > PLAYER_MAX_POSITION) {
    return t("metadata.notifications.max_position", { max: PLAYER_MAX_POSITION });
  }
  return undefined;
}

export function createAddDialog(options: {
  playersApi: PlayersApi;
  sportsApi: SportsApi;
  categoriesApi: CategoriesApi;
  tournamentsApi: TournamentsApi;
  teamsApi: TeamsApi;
}): DialogConfig {
  const { playersApi, sportsApi, categoriesApi, tournamentsApi, teamsApi } = options;

  return {
    id: "add-player",
    title: "Add Player",
    trigger: { type: "fab" },
    layout: { type: "popover" },
    structure: {
      type: "stepper",
      steps: [
        {
          id: "main",
          children: [
            {
              type: "group",
              name: "main",
              label: "Main",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  placeholder: "Select status",
                  options: playerStatusOptions,
                  defaultValue: 1,
                },
                {
                  type: "text-field",
                  name: "name",
                  label: "Name",
                  required: true,
                  placeholder: "Enter a name",
                },
                {
                  type: "text-field",
                  name: "info",
                  label: "Info",
                  placeholder: "Enter info",
                },
                {
                  type: "component",
                  component: () =>
                    import("@/v2/kits/supplemental-names/components/SupplementalNamesFields.vue"),
                  props: { mode: "add", entity: "PLAYER" },
                },
              ],
            },
            {
              type: "group",
              name: "prematchGame",
              label: "Pre-match",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  placeholder: "Select pre-match status",
                  required: true,
                  options: playerGamesStatusOptions,
                },
                {
                  type: "text-field",
                  name: "position",
                  label: "Position",
                  placeholder: "Enter position",
                  validate: positionValidator,
                },
              ],
            },
            {
              type: "group",
              name: "liveGame",
              label: "Live",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: playerGamesStatusOptions,
                  placeholder: "Select live status",
                },
                {
                  type: "text-field",
                  name: "position",
                  label: "Position",
                  placeholder: "Enter position",
                  validate: positionValidator,
                },
              ],
            },
            {
              type: "component",
              component: () => import("./assignments/PlayerAssignmentsFields.vue"),
              props: {
                mode: "add",
                sportsApi,
                categoriesApi,
                tournamentsApi,
                teamsApi,
              },
            },
          ],
        },
      ],
    },
    api: {
      submit: (data) => {
        if (!hasPlayerAssignment(data)) {
          notify({
            variant: NXGDSStatusVariant.Negative,
            text: t("metadata.notifications.assign_player_to_team_or_tournament"),
          });
          throw new Error(t("metadata.notifications.assign_player_to_team_or_tournament"));
        }

        const main = (data.main ?? {}) as Record<string, unknown>;
        const prematchGame = (data.prematchGame ?? {}) as Record<string, unknown>;
        const liveGame = (data.liveGame ?? {}) as Record<string, unknown>;
        const assignments = buildAddPlayerAssignmentsPayload(data);
        const model: Record<string, unknown> = {
          status: main.status,
          name: main.name,
          info: main.info,
          supplementalNames: buildAddSupplementalNamesPayload(data, "PLAYER"),
          prematchGame: {
            status: prematchGame.status,
            position: prematchGame.position,
          },
          liveGame: {
            status: liveGame.status,
            position: liveGame.position,
          },
          teamPlayers: assignments.teamPlayers,
          tournamentPlayers: assignments.tournamentPlayers,
        };

        return playersApi.create(model);
      },
    },
  };
}
